import { Cloud, Save, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * SourceChip — inline badge in the Viewer title bar that tells the user
 * where this document lives and what ⌘S is about to do.
 *
 *   'cloud'  + 'synced'   → green "Synced"
 *   'cloud'  + 'saving'   → amber pulsing "Saving…"
 *   'cloud'  + 'error'    → red "Sync error" (click → Manage)
 *   'device' (cloud avail)→ grey "Local · Move to cloud →"
 *   'device' (no cloud)   → grey "Local"
 *   'unsaved'             → coral "Unsaved"  (dirty, not yet persisted)
 *
 * The component is deliberately dumb — the Viewer derives source/status
 * and the cloud-availability signal from `docSource + cloudState + hasUnsavedChanges`.
 */
export default function SourceChip({
  source = 'device',         // 'cloud' | 'device' | 'unsaved'
  status = 'synced',          // 'synced' | 'saving' | 'error'
  cloudAvailable = false,     // whether the user can move this doc to cloud
  onMoveToCloud,              // handler for device→cloud affordance
  onViewCloud,                // handler for "Manage in cloud" on errors
}) {
  const { t } = useTranslation();
  if (source === 'cloud') {
    if (status === 'saving') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 select-none">
          <Cloud size={12} />
          {t('common:cloud.saving', 'Saving…')}
          <span
            className="w-1.5 h-1.5 rounded-full bg-accent-amber ml-0.5"
            style={{ animation: 'sc-pulse 1.6s ease-in-out infinite' }}
          />
          <style>{`@keyframes sc-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }`}</style>
        </span>
      );
    }
    if (status === 'error') {
      return (
        <button
          onClick={onViewCloud}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
          title={t('common:cloud.sync_error', 'Sync error')}
        >
          <AlertCircle size={12} />
          {t('common:cloud.sync_error', 'Sync error')}
          <span className="w-1.5 h-1.5 rounded-full bg-accent-red ml-0.5" />
        </button>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold border border-emerald-200 text-emerald-700 select-none"
        style={{ background: 'rgba(16, 185, 129, 0.10)' }}
        title={t('common:cloud.synced', 'Synced')}
      >
        <Cloud size={12} />
        {t('common:cloud.synced', 'Synced')}
        <span className="w-1.5 h-1.5 rounded-full bg-accent-green ml-0.5" />
      </span>
    );
  }

  if (source === 'unsaved') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold border"
        style={{ background: '#FEF2F2', color: '#B91C1C', borderColor: '#FECACA' }}
        title={t('common:cloud.unsaved', 'Unsaved')}
      >
        <AlertCircle size={12} />
        {t('common:cloud.unsaved', 'Unsaved')}
        <span className="w-1.5 h-1.5 rounded-full bg-accent-coral ml-0.5" />
      </span>
    );
  }

  if (cloudAvailable && onMoveToCloud) {
    return (
      <button
        onClick={onMoveToCloud}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 hover:text-brand-blue transition-colors group"
        title={t('common:cloud.move_to_cloud', 'Move to cloud →')}
      >
        <Save size={12} />
        {t('common:cloud.local', 'Local')}
        <span className="text-gray-400">·</span>
        <span className="text-brand-blue group-hover:underline">{t('common:cloud.move_to_cloud', 'Move to cloud →')}</span>
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-200 select-none">
      <Save size={12} />
      {t('common:cloud.local', 'Local')}
    </span>
  );
}
